// Player profile + portfolio. Editable form + career aggregate stat tiles.
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const FIELDS = [
  { k: "name", label: "Full Name", type: "text", testid: "profile-name-input" },
  { k: "age", label: "Age", type: "number", testid: "profile-age-input" },
  { k: "gender", label: "Gender", type: "select", testid: "profile-gender-input" },
  { k: "height_cm", label: "Height (cm)", type: "number", testid: "profile-height-input" },
  { k: "position", label: "Position", type: "select", testid: "profile-position-input" },
  { k: "location", label: "Location", type: "text", testid: "profile-location-input" },
  { k: "instagram", label: "Instagram Profile Link", type: "text", testid: "profile-instagram-input" },
];

const GENDER_OPTIONS = [
  { value: "", label: "Select Gender" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
];

const POSITION_OPTIONS = [
  { value: "", label: "Select Position" },
  { value: "Point Guard (PG)", label: "Point Guard (PG)" },
  { value: "Shooting Guard (SG)", label: "Shooting Guard (SG)" },
  { value: "Small Forward (SF)", label: "Small Forward (SF)" },
  { value: "Power Forward (PF)", label: "Power Forward (PF)" },
  { value: "Center (C)", label: "Center (C)" },
];

const ACHIEVEMENT_OPTIONS = [
  "Played for National Team",
  "Played Nationals",
  "Played State",
  "Played District",
  "Played University",
];

export default function Profile() {
  const { user, setUser, loading } = useAuth();
  const [form, setForm] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Local state to handle the active selection mechanics
  const [selectedTier, setSelectedTier] = useState("");
  const [customText, setCustomText] = useState("");

  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    setForm({
      name: user.name || "",
      age: user.age || "",
      gender: user.gender || "",
      height_cm: user.height_cm || "",
      position: user.position || "",
      location: user.location || "",
      bio: user.bio || "",
      instagram: user.instagram || "",
      achievements: user.achievements || []
    });
    api.get(`/players/${user.id}/portfolio`)
      .then(r => { if (alive) setPortfolio(r.data); })
      .catch(err => console.warn("[profile] portfolio fetch failed", err));
    return () => { alive = false; };
  }, [user]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-12 text-zinc-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const validateForm = () => {
    const tempErrors = {};
    if (!form.name.trim()) tempErrors.name = "Full Name is required";

    if (!form.gender?.trim()) tempErrors.gender = "Gender is required";

    if (form.age !== "" && form.age !== null) {
      const ageNum = Number(form.age);
      if (isNaN(ageNum) || ageNum <= 0 || ageNum > 120) tempErrors.age = "Please enter a valid age";
    }

    if (form.height_cm !== "" && form.height_cm !== null) {
      const heightNum = Number(form.height_cm);
      if (isNaN(heightNum) || heightNum <= 50 || heightNum > 300) tempErrors.height_cm = "Please enter a valid height";
    }
    if (form.instagram && form.instagram.trim() !== "") {
      const instagramValue = form.instagram.trim().toLowerCase();
      
      // Checks strictly for the presence of the domain name
      if (!instagramValue.includes("instagram.com")) {
        tempErrors.instagram = "Please enter a valid Instagram URL";
      }
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please correct the errors before saving.");
      return;
    }
    
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.age === "") payload.age = null; else payload.age = Number(payload.age) || null;
      if (payload.height_cm === "") payload.height_cm = null; else payload.height_cm = Number(payload.height_cm) || null;
      
      const { data } = await api.patch("/me", payload);
      setUser(data);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Add achievement bubble handler
  const handleAddBubble = () => {
    if (form.achievements.length >= 5) {
      toast.error("Maximum of 5 achievements allowed.");
      return;
    }

    let itemToAdd = "";
    if (selectedTier === "Other") {
      if (!customText.trim()) return;
      itemToAdd = customText.trim();
    } else if (selectedTier) {
      itemToAdd = selectedTier;
    }

    if (itemToAdd && !form.achievements.includes(itemToAdd)) {
      setForm({
        ...form,
        achievements: [...form.achievements, itemToAdd]
      });
      // Reset interaction fields
      setSelectedTier("");
      setCustomText("");
    }
  };

  const handleRemoveBubble = (achievementToRemove) => {
    setForm({
      ...form,
      achievements: form.achievements.filter(a => a !== achievementToRemove)
    });
  };

  const career = portfolio?.career || {};
  const fgPct = career.fg_attempted ? ((career.fg_made / career.fg_attempted) * 100).toFixed(1) : "—";

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-[1fr_1.2fr] gap-8">
      {/* Profile form */}
      <section>
        <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-court">Profile</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">{user.name || "Complete your profile"}</h1>
        <div className="text-xs font-mono text-zinc-500 mt-1">{user.email || user.phone || "No email on file"} · role: {user.role}</div>

        {form && (
          <form onSubmit={onSave} className="mt-8 space-y-5">
            {FIELDS.map(f => (
              <label key={f.k} className="block">
                <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">{f.label}</span>
                {f.type === "select" ? (
                  <select
                    data-testid={f.testid}
                    value={form[f.k] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="w-full mt-2 bg-zinc-950 border border-zinc-800 focus:border-court py-2.5 px-3 outline-none text-white appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2371717a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>")`,
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "right 12px center",
                      backgroundSize: "16px"
                    }}
                  >
                    {(f.k === "gender" ? GENDER_OPTIONS : POSITION_OPTIONS).map(opt => (
                      <option key={opt.value} value={opt.value} className="bg-zinc-950 text-white">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    data-testid={f.testid}
                    type={f.type}
                    value={form[f.k] ?? ""}
                    readOnly={f.readOnly}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className={`w-full mt-2 bg-transparent border ${errors[f.k] ? "border-red-500 focus:border-red-500" : "border-zinc-800 focus:border-court"} py-2.5 px-3 outline-none text-white ${f.readOnly ? "opacity-70 cursor-not-allowed" : ""}`}
                  />
                )}
                {errors[f.k] && <p className="text-red-500 text-xs mt-1 font-mono">{errors[f.k]}</p>}
              </label>
            ))}
{/* Redesigned Achievements Dynamic Bubble UI */}
<div className="block pt-2">
  <span className="text-xs font-bold tracking-widest uppercase text-zinc-400 block mb-2">
    Achievements ({form.achievements.length}/5)
  </span>
  
  {form.achievements.length < 5 && (
    <div className="flex flex-col gap-2 mt-2">
      <select
        value={selectedTier}
        onChange={(e) => {
          const val = e.target.value;
          if (val === "Other") {
            setSelectedTier("Other");
            setCustomText("");
          } else if (val !== "") {
            // Immediately add standard achievement to form state on selection
            setForm(prev => ({
              ...prev,
              achievements: [...prev.achievements, val]
            }));
            // Reset select dropdown back to placeholder state
            setSelectedTier("");
          }
        }}
        className="w-full bg-zinc-950 border border-zinc-800 focus:border-court py-2.5 px-3 outline-none text-sm text-white appearance-none cursor-pointer"
        style={{
          backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2371717a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 12px center",
          backgroundSize: "16px"
        }}
      >
        <option value="">Choose an achievement level...</option>
        
        {/* Filter out already added achievements */}
        {ACHIEVEMENT_OPTIONS
          .filter(opt => !form.achievements.includes(opt))
          .map(opt => (
            <option key={opt} value={opt} className="bg-zinc-950 text-white">
              {opt}
            </option>
          ))
        }
        
        <option value="Other" className="bg-zinc-950 text-white">Other (Custom limit 50 chars)</option>
      </select>

      {/* Custom Input + Mobile-Friendly Add Button for 'Other' selection */}
      {selectedTier === "Other" && (
        <div className="flex gap-2 items-center w-full">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Type custom achievement..."
              maxLength={50}
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const trimmed = customText.trim();
                  if (trimmed && !form.achievements.includes(trimmed)) {
                    setForm(prev => ({
                      ...prev,
                      achievements: [...prev.achievements, trimmed]
                    }));
                    setCustomText("");
                    setSelectedTier(""); // Collapse input back into dropdown
                  }
                }
              }}
              className="w-full bg-transparent border border-zinc-800 focus:border-court py-2.5 px-3 text-xs outline-none text-white pr-12"
              autoFocus
            />
            <span className="absolute right-3 top-3.5 text-[10px] text-zinc-600 font-mono">
              {customText.length}/50
            </span>
          </div>

          <button
            type="button"
            onClick={() => {
              const trimmed = customText.trim();
              if (trimmed && !form.achievements.includes(trimmed)) {
                setForm(prev => ({
                  ...prev,
                  achievements: [...prev.achievements, trimmed]
                }));
                setCustomText("");
                setSelectedTier(""); // Collapse input back into dropdown
              }
            }}
            disabled={!customText.trim()}
            className="h-[38px] px-4 bg-zinc-900 border border-zinc-800 hover:border-court hover:text-court text-zinc-300 text-xs font-bold uppercase transition-all disabled:opacity-30 disabled:hover:border-zinc-800 disabled:hover:text-zinc-300"
          >
            Add
          </button>
        </div>
      )}
    </div>
  )}

  {/* Real-time Achievement Bubbles Rendering Container */}
  <div className="flex flex-wrap gap-2 mt-3 min-h-[32px]">
    {form.achievements.map((achievement) => (
      <div 
        key={achievement} 
        className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-full pl-3 pr-2 py-1 text-xs text-white group hover:border-court transition-colors"
      >
        <span>{achievement}</span>
        <button
          type="button"
          onClick={() => handleRemoveBubble(achievement)}
          className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] text-zinc-500 hover:bg-zinc-800 hover:text-red-400 transition-all"
          title="Remove achievement"
        >
          ✕
        </button>
      </div>
    ))}
    {form.achievements.length === 0 && (
      <span className="text-xs text-zinc-600 italic self-center">No achievements added yet.</span>
    )}
  </div>
</div>

            <label className="block">
              <span className="text-xs font-bold tracking-widest uppercase text-zinc-400">Bio</span>
              <textarea
                data-testid="profile-bio-input"
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                rows={4}
                className="w-full mt-2 bg-transparent border border-zinc-800 focus:border-court py-2.5 px-3 outline-none text-white"
              />
            </label>
            
            <button
              data-testid="profile-save-btn"
              type="submit"
              disabled={saving}
              className="h-12 px-6 bg-court text-white font-display font-black tracking-wider uppercase border-b-4 border-court-deep hover:bg-court-hover active:translate-y-0.5 active:border-b-0 transition-all disabled:opacity-50"
            >{saving ? "Saving…" : "Save Profile"}</button>
          </form>
        )}
      </section>

      {/* Career portfolio */}
      <section>
        <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-court">Career Portfolio</div>
        <h2 className="font-display font-black text-3xl uppercase tracking-tighter mt-2">Lifetime Statline</h2>

        <div className="mt-6 grid grid-cols-3 gap-3" data-testid="career-statline">
          {[
            ["GP", career.games || 0],
            ["PTS", career.points || 0],
            ["REB", career.rebounds || 0],
            ["AST", career.assists || 0],
            ["STL", career.steals || 0],
            ["BLK", career.blocks || 0],
            ["TO", career.turnovers || 0],
            ["PF", career.fouls || 0],
            ["FG%", fgPct],
          ].map(([k, v]) => (
            <div key={k} className="border border-zinc-800 p-4">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">{k}</div>
              <div className="font-display font-black text-3xl tracking-tighter mt-1">{v}</div>
            </div>
          ))}
        </div>

        

        <div className="mt-8">
          <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-zinc-500 mb-3">Teams & Tournaments</div>
          {portfolio?.memberships?.length ? (
            <ul className="divide-y divide-zinc-900 border border-zinc-800">
              {portfolio.memberships.map((m) => (
                <li key={`${m.tournament_id}-${m.team_id}`} className="p-3 text-sm flex justify-between">
                  <span>Tournament {m.tournament_id?.slice(0, 8)}</span>
                  <span className="text-zinc-400">Team {m.team_id?.slice(0, 8)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="border border-zinc-800 p-6 text-sm text-zinc-500">
              No tournament history yet. Once stats are recorded against your phone, they&apos;ll appear here automatically.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}